import type { EmbedCommand, EmbedNav, EmbedSection } from '@crow/ui/embed';
import { useSyncExternalStore } from 'react';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { FrameAction } from './plugin-frame';

/**
 * The host's end of the embed bridge (`@crow/ui/embed`): what an embedded Crowlingo application
 * (CrowDrive, CrowBox in a remote app's tab) says it shows, its sections, its page's title and
 * actions, kept per place it is shown in (full screen: `fullScreenSectionsKey`; docked: its view),
 * and the way to send it the host's commands (a section picked in the host's column, an action
 * of the bar, a path to open).
 */
type Bridge = {
  nav: EmbedNav | null;
  /** The page's origin (↗ opens it there). */
  origin: string | null;
  send: ((command: EmbedCommand) => void) | null;
};

const EMPTY: Bridge = { nav: null, origin: null, send: null };
const bridges = new Map<string, Bridge>();
const listeners = new Set<() => void>();

function update(key: string, change: Partial<Bridge>) {
  const next = { ...(bridges.get(key) ?? EMPTY), ...change };
  if (next.nav === null && next.origin === null && next.send === null) bridges.delete(key);
  else bridges.set(key, next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The bridge key of a remote app docked in the side panel (one tab per view). */
export function dockedBridgeKey(view: string): string {
  return `dock:${view}`;
}

/** The page shown under `key` says what it shows (null: it stopped, or was never told). */
export function setRemoteNav(key: string, nav: EmbedNav | null): void {
  update(key, { nav });
}

/** The page shown under `key` is connected: its origin and how to reach it (null when it goes). */
export function connectRemoteApp(
  key: string,
  connection: { origin: string; send(command: EmbedCommand): void } | null,
): void {
  update(
    key,
    connection === null
      ? { origin: null, send: null, nav: null }
      : { origin: connection.origin, send: connection.send },
  );
}

/** Sends a command to the page shown under `key`; false when none is connected. */
export function sendToRemoteApp(key: string, command: EmbedCommand): boolean {
  const send = bridges.get(key)?.send;
  if (send === null || send === undefined) return false;
  send(command);
  return true;
}

/** What the page shown under `key` says, and its origin. */
export function useRemoteApp(key: string | null): Bridge {
  return useSyncExternalStore(
    subscribe,
    () => (key === null ? EMPTY : (bridges.get(key) ?? EMPTY)),
    () => EMPTY,
  );
}

/** The app's actions, as the host's bar draws them; a click reaches the page. */
export function remoteFrameActions(key: string, nav: EmbedNav | null): FrameAction[] {
  return (nav?.actions ?? []).map((action) => ({
    id: action.id,
    label: action.label,
    ...(action.icon === undefined ? {} : { icon: pluginIcon(action.icon) }),
    primary: action.primary === true,
    disabled: action.disabled,
    ...(action.menu === undefined ? {} : { menu: action.menu }),
    onSelect: (choice: string) =>
      sendToRemoteApp(key, {
        type: 'crow:action',
        id: action.id,
        ...(action.menu === undefined ? {} : { choice }),
      }),
  }));
}

/** The app's sections, by group (the first, unnamed, first), in the order it sent them. */
export function groupedSections(
  nav: EmbedNav | null,
): { group: string | null; sections: EmbedSection[] }[] {
  const groups: { group: string | null; sections: EmbedSection[] }[] = [];
  for (const section of nav?.sections ?? []) {
    const group = section.group ?? null;
    const existing = groups.find((entry) => entry.group === group);
    if (existing === undefined) groups.push({ group, sections: [section] });
    else existing.sections.push(section);
  }
  return groups;
}

/**
 * Where ↗ opens the app full screen, in the user's browser: where it is now (`nav.path`), on its
 * origin, without the host's `crow_embedded` (it then shows its own bar and column).
 */
export function remoteAppBrowserUrl(
  origin: string | null,
  path?: string | undefined,
): string | null {
  if (origin === null) return null;
  try {
    const url = new URL(path ?? '/', origin);
    if (url.origin !== origin) return null;
    url.searchParams.delete('crow_embedded');
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The deep link of an item of a remote app (a CrowDrive file, a CrowBox thread), as its tab
 * opens it docked: its path, query and hash. An absolute URL keeps only those (the tab opens on
 * the app's own origin, resolved by the server).
 */
export function remoteItemPath(url: string): string | null {
  try {
    const parsed = new URL(url, 'https://crowdex.invalid');
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return /^\/(?![/\\])/.test(path) ? path : null;
  } catch {
    return null;
  }
}
